import { StorageService, API_BASE_URL, safeParseJson } from './storageService';

export interface CertOverviewItem {
  certId: string;
  name: string;
  shortName: string;
  gatewayCategory: string;
  categories: string[];
  modules: Record<string, boolean>;
  counts: {
    questions: number;
    flashcards: number;
    skills: number;
    studyModules: number;
    simulators: number;
    totalItems: number;
  };
  latestReport: {
    id: string;
    healthScore: number;
    clinicalAccuracyScore: number;
    distractorRigorScore: number;
    boardAlignmentScore: number;
    evaluatedAt: string;
    criticalIssues: number;
    moderateIssues: number;
    minorIssues: number;
  } | null;
  pendingPatchesCount: number;
}

export interface CertAuditPatchDiff {
  field: string;
  before: any;
  after: any;
}

export interface CertAuditPatchItem {
  _id: string;
  reportId?: string;
  certId: string;
  targetCollection: 'questions' | 'flashcards' | 'skills' | 'studymodules' | 'simulatorscenarios';
  targetDocumentId: string;
  itemTitle: string;
  issueCategory: 'clinical_accuracy' | 'outdated_guideline' | 'weak_distractor' | 'missing_rationale' | 'critical_safety_gap' | 'calculation_error' | 'pedagogy_structure';
  severity: 'critical' | 'moderate' | 'minor';
  finding: string;
  clinicalCitation: string;
  suggestedAction: string;
  diff: CertAuditPatchDiff[];
  status: 'pending' | 'applied' | 'rejected';
  appliedAt?: string;
  appliedBy?: string;
  createdAt: string;
}

export interface CertAuditReportItem {
  _id: string;
  certId: string;
  targetModule: string;
  healthScore: number;
  clinicalAccuracyScore: number;
  distractorRigorScore: number;
  boardAlignmentScore: number;
  cognitiveDistribution: {
    recall: number;
    application: number;
    clinicalJudgment: number;
  };
  totalEvaluated: number;
  criticalIssuesCount: number;
  moderateIssuesCount: number;
  minorIssuesCount: number;
  summaryText: string;
  benchmarkInsights: {
    officialBoardBody?: string;
    latestGuidelineRevisions?: string[];
    curriculumGaps?: string[];
    competitorAdvantages?: string[];
  };
  status: string;
  createdAt: string;
}

export interface CompetitorBenchmarkData {
  testingVendors: string[];
  domainWeights: { domain: string; weight: string }[];
  recentRegulatoryChanges: string[];
  competitorPedagogyStrengths: string[];
  criticalFailFocalPoints: string[];
}

export const CertAuditorService = {
  getOverview: async (): Promise<{ success: boolean; certifications: CertOverviewItem[] }> => {
    const res = await fetch(`${API_BASE_URL}/admin/cert-auditor/overview`, {
      headers: StorageService.getAuthHeader()
    });
    if (!res.ok) {
      const err = await safeParseJson(res).catch(() => ({ error: 'Failed to fetch overview' }));
      throw new Error(err.error || 'Failed to fetch overview');
    }
    return await safeParseJson(res);
  },

  runScan: async (payload: {
    certId: string;
    moduleScope?: string;
    sampleLimit?: number;
    focusArea?: string;
  }): Promise<{
    success: boolean;
    report: CertAuditReportItem;
    patches: CertAuditPatchItem[];
    evaluatedCount: number;
  }> => {
    const res = await fetch(`${API_BASE_URL}/admin/cert-auditor/scan`, {
      method: 'POST',
      headers: {
        ...StorageService.getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    if (!res.ok) {
      const err = await safeParseJson(res).catch(() => ({ error: 'Scan failed' }));
      throw new Error(err.error || 'Scan failed');
    }
    return await safeParseJson(res);
  },

  scanCompetitorBenchmarks: async (certId: string): Promise<{
    success: boolean;
    certId: string;
    benchmark: CompetitorBenchmarkData;
  }> => {
    const res = await fetch(`${API_BASE_URL}/admin/cert-auditor/benchmark-scan`, {
      method: 'POST',
      headers: {
        ...StorageService.getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ certId })
    });
    if (!res.ok) {
      const err = await safeParseJson(res).catch(() => ({ error: 'Benchmark scan failed' }));
      throw new Error(err.error || 'Benchmark scan failed');
    }
    return await safeParseJson(res);
  },

  commitPatch: async (patchId: string): Promise<{ success: boolean; patch: CertAuditPatchItem }> => {
    const res = await fetch(`${API_BASE_URL}/admin/cert-auditor/commit-patch`, {
      method: 'POST',
      headers: {
        ...StorageService.getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ patchId })
    });
    if (!res.ok) {
      const err = await safeParseJson(res).catch(() => ({ error: 'Commit patch failed' }));
      throw new Error(err.error || 'Commit patch failed');
    }
    return await safeParseJson(res);
  },

  commitBatch: async (patchIds: string[]): Promise<{
    success: boolean;
    appliedCount: number;
    failedCount: number;
    errors?: any[];
  }> => {
    const res = await fetch(`${API_BASE_URL}/admin/cert-auditor/commit-batch`, {
      method: 'POST',
      headers: {
        ...StorageService.getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ patchIds })
    });
    if (!res.ok) {
      const err = await safeParseJson(res).catch(() => ({ error: 'Batch commit failed' }));
      throw new Error(err.error || 'Batch commit failed');
    }
    return await safeParseJson(res);
  },

  rejectPatch: async (patchId: string, reason?: string): Promise<{ success: boolean }> => {
    const res = await fetch(`${API_BASE_URL}/admin/cert-auditor/reject-patch`, {
      method: 'POST',
      headers: {
        ...StorageService.getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ patchId, reason })
    });
    if (!res.ok) {
      const err = await safeParseJson(res).catch(() => ({ error: 'Reject patch failed' }));
      throw new Error(err.error || 'Reject patch failed');
    }
    return await safeParseJson(res);
  },

  getPatches: async (certId: string, status: 'all' | 'pending' | 'applied' | 'rejected' = 'all'): Promise<{
    success: boolean;
    patches: CertAuditPatchItem[];
  }> => {
    const res = await fetch(`${API_BASE_URL}/admin/cert-auditor/patches/${encodeURIComponent(certId)}?status=${status}`, {
      headers: StorageService.getAuthHeader()
    });
    if (!res.ok) {
      const err = await safeParseJson(res).catch(() => ({ error: 'Failed to fetch patches' }));
      throw new Error(err.error || 'Failed to fetch patches');
    }
    return await safeParseJson(res);
  },

  getHistory: async (certId: string): Promise<{
    success: boolean;
    reports: CertAuditReportItem[];
  }> => {
    const res = await fetch(`${API_BASE_URL}/admin/cert-auditor/history/${encodeURIComponent(certId)}`, {
      headers: StorageService.getAuthHeader()
    });
    if (!res.ok) {
      const err = await safeParseJson(res).catch(() => ({ error: 'Failed to fetch audit history' }));
      throw new Error(err.error || 'Failed to fetch audit history');
    }
    return await safeParseJson(res);
  },

  auditSingleItem: async (certId: string, collectionType: string, item: any): Promise<any> => {
    const res = await fetch(`${API_BASE_URL}/admin/cert-auditor/audit-item`, {
      method: 'POST',
      headers: {
        ...StorageService.getAuthHeader(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ certId, collectionType, item })
    });
    if (!res.ok) {
      const err = await safeParseJson(res).catch(() => ({ error: 'Item audit failed' }));
      throw new Error(err.error || 'Item audit failed');
    }
    return await safeParseJson(res);
  }
};
